import { FaRegFilePdf } from "react-icons/fa6";
import { MdOutlineMailOutline } from "react-icons/md";
import { SiMicrosoftexcel } from "react-icons/si";

import { Button } from "components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { Separator } from "components/ui/separator";
import { toast } from "components/ui/use-toast";
import {
  useExportConciliationDemonstrativeExcelMutation,
  useExportConciliationDemonstrativePdfMutation,
} from "@/services/store-conciliation";
import { IDemonstrativeExportQueued } from "src/@types/store-conciliation";

import {
  DEMONSTRATIVE_EXPORT_LABELS,
  DEMONSTRATIVE_EXPORT_MESSAGES,
  DEMONSTRATIVE_EXPORT_TOAST_DURATION,
  DemonstrativeExportProps,
  EMAIL_PLACEHOLDER,
  exportButtonClass,
  iconContainerClass,
} from "./types";

type ExportKind = "excel" | "pdf";

export default function DemonstrativeExport({
  conciliation_id,
}: DemonstrativeExportProps) {
  const [exportExcel, { isLoading: isExportingExcel }] =
    useExportConciliationDemonstrativeExcelMutation();
  const [exportPdf, { isLoading: isExportingPdf }] =
    useExportConciliationDemonstrativePdfMutation();

  const isExporting: boolean = isExportingExcel || isExportingPdf;

  const handleExport = async (kind: ExportKind): Promise<void> => {
    if (!conciliation_id) return;

    try {
      const queued: IDemonstrativeExportQueued =
        kind === "excel"
          ? await exportExcel({ conciliationId: conciliation_id }).unwrap()
          : await exportPdf({ conciliationId: conciliation_id }).unwrap();

      toast({
        description: DEMONSTRATIVE_EXPORT_MESSAGES.queued.replace(
          EMAIL_PLACEHOLDER,
          queued.email,
        ),
        duration: DEMONSTRATIVE_EXPORT_TOAST_DURATION,
      });
    } catch {
      toast({
        variant: "destructive",
        description:
          kind === "excel"
            ? DEMONSTRATIVE_EXPORT_MESSAGES.excelError
            : DEMONSTRATIVE_EXPORT_MESSAGES.pdfError,
        duration: DEMONSTRATIVE_EXPORT_TOAST_DURATION,
      });
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={isExporting || !conciliation_id}
          className="flex items-center gap-2 transition-all duration-200"
        >
          <MdOutlineMailOutline className="w-4 h-4" />
          {isExporting
            ? DEMONSTRATIVE_EXPORT_LABELS.exporting
            : DEMONSTRATIVE_EXPORT_LABELS.trigger}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 bg-white shadow-lg border">
        <div className="space-y-1">
          <div className="px-2 py-1 text-xs font-medium text-[#2B0C1A] uppercase tracking-wide">
            {DEMONSTRATIVE_EXPORT_LABELS.formatsTitle}
          </div>
          <button
            onClick={() => handleExport("excel")}
            disabled={isExporting}
            className={exportButtonClass}
          >
            <div className={iconContainerClass}>
              <SiMicrosoftexcel className="w-4 h-4 text-[#2B0C1A]" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-[#2B0C1A]">
                {DEMONSTRATIVE_EXPORT_LABELS.excelTitle}
              </div>
              <div className="text-xs text-gray-600">
                {DEMONSTRATIVE_EXPORT_LABELS.excelHint}
              </div>
            </div>
          </button>

          <Separator className="my-2 bg-[#2B0C1A]/20" />

          <button
            onClick={() => handleExport("pdf")}
            disabled={isExporting}
            className={exportButtonClass}
          >
            <div className={iconContainerClass}>
              <FaRegFilePdf className="w-4 h-4 text-[#2B0C1A]" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-[#2B0C1A]">
                {DEMONSTRATIVE_EXPORT_LABELS.pdfTitle}
              </div>
              <div className="text-xs text-gray-600">
                {DEMONSTRATIVE_EXPORT_LABELS.pdfHint}
              </div>
            </div>
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
